// ════════════════════════════════════════════════════════════════════════
//  Inhalte laden
//
//  Rangfolge:
//    1. eingebackene Fassung (Build-Zeit) – trägt den Gastmodus
//    2. Fassung der Klasse, aus dem Cache
//    3. Fassung der Klasse, frisch geladen
//
//  Der Gastmodus darf ohne einen einzigen Request funktionieren. Deshalb ist die
//  eingebackene Fassung nicht der Notnagel, sondern der Normalfall – geladen wird
//  nur, wenn eine Klasse eine bestimmte Fassung vorgibt.
//
//  Gibt sie eine vor, wird sie auch geladen. Keine Abkürzung „das haben wir doch
//  schon“: welche Fassung eine Klasse übt, entscheidet die Lehrkraft, nicht der
//  Zufall, dass der App-Build zufällig dieselbe Nummer trägt.
// ════════════════════════════════════════════════════════════════════════

import { OTTO_BUNDLE, parseBundle, type Bundle } from "@otto/lehrgang";

/** Zum Build-Zeitpunkt aktuelle Fassung. Immer vorhanden, immer gültig. */
export const BUILTIN_BUNDLE: Bundle = OTTO_BUNDLE;

export type Origin =
  | { readonly kind: "builtin" }
  | { readonly kind: "cache"; readonly version: number }
  | { readonly kind: "fetched"; readonly version: number }
  /** Laden fehlgeschlagen – die Kinder üben mit der eingebackenen Fassung weiter. */
  | { readonly kind: "fallback"; readonly reason: string };

export type Content = { readonly bundle: Bundle; readonly herkunft: Origin };

const cacheKey = (slug: string, version: number) => `otto.bundle.${slug}.v${version}`;

/**
 * localStorage kann werfen (privater Modus, blockierte Website-Daten) und leer
 * zurückkommen. Beides ist normal und darf die App nicht anhalten.
 */
function readCache(queryKeys: string): unknown {
  try {
    const raw = globalThis.localStorage?.getItem(queryKeys);
    return raw === null || raw === undefined ? null : JSON.parse(raw);
  } catch {
    return null;
  }
}

function writeCache(queryKeys: string, value: unknown): void {
  try {
    globalThis.localStorage?.setItem(queryKeys, JSON.stringify(value));
  } catch {
    // Kein Cache ist kein Fehler, nur langsamer.
  }
}

/**
 * Basisadresse der veröffentlichten Bundles.
 *
 * Sie liegen im öffentlichen Storage-Bucket, den die Admin-App beim
 * Veröffentlichen füllt. Aus der Supabase-URL abgeleitet, damit nicht zwei
 * Adressen konfiguriert werden müssen, die auseinanderlaufen können.
 */
export function bundleBaseFrom(env: Record<string, string | undefined>): string | null {
  const explicit = env["VITE_CONTENT_BASE"];
  if (explicit) return explicit.replace(/\/$/, "");
  const url = env["VITE_SUPABASE_URL"];
  if (!url) return null;
  return `${url.replace(/\/$/, "")}/storage/v1/object/public/inhalte`;
}

export type LoadOptions = {
  /** Ohne Angabe wird nichts geladen – Gastmodus. */
  readonly version?: { readonly slug: string; readonly version: number };
  /** Basis-URL der veröffentlichten Bundles, z. B. "/inhalte". */
  readonly baseUrl?: string;
  readonly fetchFn?: typeof fetch;
  readonly signal?: AbortSignal;
};

/**
 * Liefert immer ein gültiges Bundle – notfalls das eingebackene. Eine Klasse mit
 * einem kaputten Bundle stehen zu lassen wäre schlimmer als eine veraltete
 * Fassung: die Kinder sitzen davor und die Stunde läuft.
 */
export async function loadBundle(optionen: LoadOptions = {}): Promise<Content> {
  const { version } = optionen;
  if (!version) return { bundle: BUILTIN_BUNDLE, herkunft: { kind: "builtin" } };

  // Hier stand eine Abkürzung: entspricht die verlangte Fassung der
  // eingebackenen, nimm die eingebackene. Die Nummer dafür war fest verdrahtet
  // (BUILTIN_VERSION = 1) und wurde von niemandem gepflegt - eine Klasse auf
  // Fassung 1 bekam damit den App-Build statt der veröffentlichten Fassung 1,
  // und zwar stillschweigend.
  //
  // Gespart hat sie fast nichts: der Gastmodus kommt hier gar nicht an, weil
  // dort keine Fassung gesetzt ist, und alles andere liegt nach dem ersten Mal
  // im Cache.

  const queryKeys = cacheKey(version.slug, version.version);
  const cached = readCache(queryKeys);
  if (cached !== null) {
    const checked = parseBundle(cached);
    if ("bundle" in checked) {
      return {
        bundle: checked.bundle,
        herkunft: { kind: "cache", version: version.version },
      };
    }
    // Kaputter Cache-Eintrag: wegwerfen und neu holen.
    try {
      globalThis.localStorage?.removeItem(queryKeys);
    } catch {
      /* siehe schreibeCache */
    }
  }

  const fetchFn = optionen.fetchFn ?? globalThis.fetch;
  const baseUrl = (optionen.baseUrl ?? "/inhalte").replace(/\/$/, "");
  const url = `${baseUrl}/${version.slug}/v${version.version}.json`;

  try {
    const response = await fetchFn(
      url,
      optionen.signal ? { signal: optionen.signal } : {},
    );
    if (!response.ok) {
      return fallbackContent(
        `Fassung ${version.version} nicht abrufbar (${response.status})`,
      );
    }
    const checked = parseBundle(await response.json());
    if (!("bundle" in checked)) {
      const firstMessage = checked.problems[0]?.message ?? "unbekannter Fehler";
      return fallbackContent(`Fassung ${version.version} ist ungültig: ${firstMessage}`);
    }
    writeCache(queryKeys, checked.bundle);
    return {
      bundle: checked.bundle,
      herkunft: { kind: "fetched", version: version.version },
    };
  } catch (error) {
    return fallbackContent(error instanceof Error ? error.message : "Netzfehler");
  }
}

function fallbackContent(reason: string): Content {
  return { bundle: BUILTIN_BUNDLE, herkunft: { kind: "fallback", reason } };
}
